/**
 * Browser geolocation + reverse geocoding (OpenStreetMap Nominatim).
 * Requires HTTPS (or localhost). User sees the browser's location permission prompt.
 */

/** localStorage: "done" | "skipped" once user completes or dismisses first-visit prompt */
export const LOCATION_ONBOARDING_SESSION_KEY = "gem_location_onboarding_v1";

/** localStorage: JSON snapshot for checkout prefill */
export const DELIVERY_LOCATION_SNAPSHOT_KEY = "gem_delivery_location_snapshot";

export function getLocationOnboardingState() {
  try {
    return localStorage.getItem(LOCATION_ONBOARDING_SESSION_KEY);
  } catch {
    return null;
  }
}

export function setLocationOnboardingState(value) {
  try {
    localStorage.setItem(LOCATION_ONBOARDING_SESSION_KEY, value);
  } catch (_) {}
}

export function saveDeliveryLocationSnapshot(payload) {
  try {
    localStorage.setItem(
      DELIVERY_LOCATION_SNAPSHOT_KEY,
      JSON.stringify({ ...payload, fetchedAt: Date.now() })
    );
  } catch (_) {}
}

export function getDeliveryLocationSnapshot() {
  try {
    const raw = localStorage.getItem(DELIVERY_LOCATION_SNAPSHOT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** True if we already saved coords or enough address fields (e.g. from cart / earlier success). */
export function hasStoredDeliveryLocation() {
  const s = getDeliveryLocationSnapshot();
  if (!s || typeof s !== "object") return false;
  const lat = Number(s.lat);
  const lng = Number(s.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return true;
  if (s.pincode != null && String(s.pincode).trim() !== "") return true;
  if (s.city != null && String(s.city).trim() !== "") return true;
  if (s.line1 != null && String(s.line1).trim() !== "") return true;
  return false;
}

export function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      reject(new Error("Location is not available in this browser."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        }),
      (err) => {
        if (err.code === 1) {
          reject(new Error("Location permission denied. Allow location in your browser or enter your address manually."));
        } else if (err.code === 2) {
          reject(new Error("Your position could not be determined."));
        } else if (err.code === 3) {
          reject(new Error("Location request timed out. Try again or enter your address manually."));
        } else {
          reject(new Error(err.message || "Could not get location."));
        }
      },
      { enableHighAccuracy: true, timeout: 25000, maximumAge: 120000 }
    );
  });
}

/**
 * Reverse geocode to delivery fields (India-friendly; works worldwide).
 * @returns {{ pincode: string, city: string, state: string, line1: string }}
 */
export async function reverseGeocodeLatLng(latitude, longitude) {
  const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${encodeURIComponent(
    latitude
  )}&lon=${encodeURIComponent(longitude)}&addressdetails=1`;
  const res = await fetch(url, {
    headers: {
      Accept: "application/json",
    },
  });
  if (!res.ok) {
    throw new Error("Could not resolve address from your location.");
  }
  const data = await res.json();
  const a = data.address || {};
  const pinDigits = String(a.postcode || "").replace(/\D/g, "").slice(0, 8);
  const city =
    a.city ||
    a.town ||
    a.village ||
    a.municipality ||
    a.county ||
    a.state_district ||
    "";
  const state = a.state || "";
  const parts = [a.road, a.neighbourhood, a.suburb, a.quarter].filter(Boolean);
  const line1 = parts.length ? parts.join(", ") : (data.display_name || "").split(",").slice(0, 2).join(", ").trim();
  return {
    pincode: pinDigits,
    city: String(city).trim(),
    state: String(state).trim(),
    line1: line1 || String(city || "Delivery location").trim(),
  };
}
